import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge, Input, Label } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useListParams } from "hooks/useListParams"
import { useAccountLoans, useAccountLoan, useDeleteAccountLoan } from "hooks/useAccountLoan"
import { useAccountLoanStatuses } from "hooks/useAccountLoanStatus"
import { useAccounts } from "hooks/useAccountEntity"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { downloadAndOpenFile } from "api/pdfApi"
import AccountFilterPanel from "pages/AccountPages/utils/AccountFilterPanel"

import AccountLoanAddEdit from "./AccountLoanAddEdit"
import AccountLoanHeader from "./AccountLoanHeader"
import AccountLoanView from "./AccountLoanView"

const AccountLoanList = () => {
  document.title = "Account Loan List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [filters, setFilters] = useState({ date_from: "", date_to: "", borrower_type: "", account_id: "", loan_status_id: "" })
  const [isExporting, setIsExporting] = useState(false)
  const [isExportingCsv, setIsExportingCsv] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({ page: 1, search: "" })

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleFilterReset = useCallback(() => {
    setFilters({ date_from: "", date_to: "", borrower_type: "", account_id: "", loan_status_id: "" })
  }, [])

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return {
      page,
      per_page,
      search,
      ...(filters.date_from && { date_from: filters.date_from }),
      ...(filters.date_to && { date_to: filters.date_to }),
      ...(filters.borrower_type && { borrower_type: filters.borrower_type }),
      ...(filters.account_id && { account_id: filters.account_id }),
      ...(filters.loan_status_id && { loan_status_id: filters.loan_status_id }),
    }
  }, [params, filters])

  const { data: responseData, isLoading, refetch, isRefetching } = useAccountLoans(queryParams)
  const { data: viewResponse, isLoading: isViewLoading } = useAccountLoan(selectedViewId, viewModal)
  const { mutateAsync: deleteItem, isPending: deleteLoading } = useDeleteAccountLoan()

  const handleExportPdf = () =>
    downloadAndOpenFile({ url: "/account-exports/loans", params: queryParams, openInNewTab: false, filename: "loans.pdf", onStart: () => setIsExporting(true), onEnd: () => setIsExporting(false) })

  const handleExportCsv = () =>
    downloadAndOpenFile({ url: "/account-exports/loans/csv", params: queryParams, mimeType: "text/csv", openInNewTab: false, filename: "loans.csv", onStart: () => setIsExportingCsv(true), onEnd: () => setIsExportingCsv(false) })

  const { data: loanStatusResponse } = useAccountLoanStatuses({ all: true, per_page: 200 })
  const { data: accountResponse } = useAccounts({ all: true, per_page: 200 })
  const loanStatusList = loanStatusResponse?.data?.data || loanStatusResponse?.data || []
  const accountList = accountResponse?.data?.data || accountResponse?.data || []

  const toggleViewModal = useCallback(() => {
    setViewModal(prev => !prev)
    if (viewModal) setSelectedViewId(null)
  }, [viewModal])

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleViewClick = item => {
    setSelectedViewId(item?.id || null)
    setViewModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Loan deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch {
      toast.error("Failed to delete loan.")
    }
  }

  const columns = useMemo(() => [
    { header: "ID", accessorKey: "id" },
    {
      header: "Borrower",
      cell: ({ row }) => (
        <div className="d-flex flex-column">
          <span>{row.original.borrower_label || "-"}</span>
          {row.original.borrower_type && (
            <Badge color="light" className="text-dark align-self-start mt-1 text-capitalize">
              {row.original.borrower_type}
            </Badge>
          )}
        </div>
      ),
    },
    {
      header: "Amount",
      cell: ({ row }) => {
        const currency = row.original.account?.currency || "BDT"
        return (
          <div className="d-flex flex-wrap gap-1 align-items-center">
            <Badge color="light" className="text-dark">{formatCurrency(row.original.amount || 0, currency)}</Badge>
            {row.original.is_reversed && <Badge color="secondary">Reversed</Badge>}
          </div>
        )
      },
    },
    {
      header: "Remaining",
      cell: ({ row }) => {
        if (row.original.remaining == null) return "-"
        const currency = row.original.account?.currency || "BDT"
        const isZero = row.original.remaining === 0
        return (
          <Badge color={isZero ? "success" : "warning"} className="text-dark">
            {formatCurrency(row.original.remaining, currency)}
          </Badge>
        )
      },
    },
    {
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.loan_status?.name || row.original.loanStatus?.name || "-"
        const slug = row.original.loan_status?.slug || row.original.loanStatus?.slug || ""
        const color = slug === "active" ? "primary" : slug === "closed" ? "success" : slug === "overdue" ? "danger" : "secondary"
        return <Badge color={color}>{status}</Badge>
      },
    },
    { header: "Account", cell: ({ row }) => row.original.account?.name || "-" },
    { header: "Loan Date", cell: ({ row }) => row.original.loan_date ? formatReadableDate(row.original.loan_date) : "-" },
    { header: "Due Date", cell: ({ row }) => row.original.due_date ? formatReadableDate(row.original.due_date) : "-" },
    {
      header: "Action",
      cell: ({ row }) => (
        <ul className="list-unstyled hstack gap-1 mb-0">
          <li>
            <Button color="primary" size="sm" onClick={() => handleViewClick(row.original)} id={`view-account-loan-${row.original.id}`}>
              <i className="mdi mdi-eye-outline" />
            </Button>
            <UncontrolledTooltip placement="top" target={`view-account-loan-${row.original.id}`}>View</UncontrolledTooltip>
          </li>
          <CanView access={[ACCESS.ACCOUNT_LOAN.EDIT]}>
            <li>
              <Button color="info" size="sm" onClick={() => handleEditClick(row.original)} id={`edit-account-loan-${row.original.id}`}>
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`edit-account-loan-${row.original.id}`}>Edit</UncontrolledTooltip>
            </li>
          </CanView>
          <CanView access={[ACCESS.ACCOUNT_LOAN.DELETE]}>
            <li>
              <Button color="danger" size="sm" onClick={() => handleDeleteClick(row.original)} id={`delete-account-loan-${row.original.id}`}>
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`delete-account-loan-${row.original.id}`}>Delete</UncontrolledTooltip>
            </li>
          </CanView>
        </ul>
      ),
    },
  ], [])

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Account Loan" breadcrumbItem="Account Loan List">
      <AccountLoanHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
        onExportPdf={handleExportPdf}
        isExporting={isExporting}
        onExportCsv={handleExportCsv}
        isExportingCsv={isExportingCsv}
      />

      <div className="pb-3" />
      <DeleteModal show={deleteModal} onDeleteClick={handleDelete} loading={deleteLoading} onCloseClick={() => setDeleteModal(false)} />

      <AccountFilterPanel filters={filters} onChange={handleFilterChange} onReset={handleFilterReset}>
        <Col xs={12} sm={6} md={3}>
          <Label className="mb-1 small fw-semibold">Borrower Type</Label>
          <Input type="select" bsSize="sm" value={filters.borrower_type || ""} onChange={e => handleFilterChange("borrower_type", e.target.value)}>
            <option value="">All Types</option>
            <option value="staff">Staff</option>
            <option value="customer">Customer</option>
            <option value="importer">Importer</option>
            <option value="vendor">Vendor</option>
            <option value="other">Other</option>
          </Input>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Label className="mb-1 small fw-semibold">Account</Label>
          <Input type="select" bsSize="sm" value={filters.account_id || ""} onChange={e => handleFilterChange("account_id", e.target.value)}>
            <option value="">All Accounts</option>
            {accountList.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Input>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Label className="mb-1 small fw-semibold">Status</Label>
          <Input type="select" bsSize="sm" value={filters.loan_status_id || ""} onChange={e => handleFilterChange("loan_status_id", e.target.value)}>
            <option value="">All Statuses</option>
            {loanStatusList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Input>
        </Col>
      </AccountFilterPanel>

      <Row className="mb-3">
        <Col sm={4}><GlobalInputSearch value={params.search} onChange={setSearch} /></Col>
      </Row>

      <TableContainer columns={columns} data={data} isPagination={true} paginationInfo={paginationInfo} onPageChange={setPage} loading={isLoading || isRefetching} />

      <AccountLoanView modal={viewModal} toggleModal={toggleViewModal} loan={viewResponse?.data} loading={isViewLoading} />

      <AccountLoanAddEdit modal={modal} isEdit={isEdit} editedData={selectedData} toggleModal={toggleModal} />
    </PageWrapper>
  )
}

export default AccountLoanList
